import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalculator, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ConversionExplanationDecimalToAll from './ConversionExplanationDecimalToAll';
import ConversionFlow from '../../ConversionFlow';

import { specialConversions } from '../../../../utils/unitConversions/number/numberUtils';

const getExampleResult = (base36Value, toUnit) => {
  const decimalValue = parseInt(base36Value, 36);
  switch (toUnit) {
    case 'Decimal':
      return decimalValue.toString();
    case 'Binary':
      return decimalValue.toString(2);
    case 'Octal':
      return decimalValue.toString(8);
    case 'Hexadecimal':
      return decimalValue.toString(16).toUpperCase();
    case 'Base-64':
      return specialConversions['Base-64'].fromDecimal(decimalValue);
    case 'Scientific Notation':
      return decimalValue.toExponential();
    default:
      return 'N/A';
  }
};

const ConversionExplanationBase36ToAll = ({ conversionType, fromUnit, toUnit, inputValue, result, darkMode, isEncodingMode, onEncodingModeToggle }) => {
  const [showIntermediateConversion, setShowIntermediateConversion] = useState(false);
  const [showDetailedStep2, setShowDetailedStep2] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizResult, setQuizResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isConversionRendered, setIsConversionRendered] = useState(false);

  const handleQuizSubmit = useCallback(() => {
    const isCorrect = quizAnswer.toLowerCase() === result.toLowerCase();
    setQuizResult(isCorrect);
  }, [quizAnswer, result]);

  const renderExampleOrConversionText = useCallback((base36Value, isExample) => (
    isExample ? (
      <p className="mb-4 text-yellow-600 italic">
        Example: Let's convert the Base-36 number {base36Value} to {toUnit}.
      </p>
    ) : (
      <p className="mb-4 text-green-600">
        Your conversion: {base36Value} (Base-36) to {toUnit}
      </p>
    )
  ), [toUnit]);

  const toggleIntermediateConversion = () => setShowIntermediateConversion(!showIntermediateConversion);
  const toggleDetailedStep2 = () => setShowDetailedStep2(!showDetailedStep2);

  const getIntermediateConversionExplanation = useCallback((base36Value) => {
    const decimalValue = parseInt(base36Value, 36);
    const steps = base36Value.split('').reverse();

    const renderBase36ToDecimalConversion = () => (
      <div className="relative mt-12 flex flex-col items-center">
        <div className="absolute top-0 left-0 text-2xl font-bold main-number bg-gray-100 dark:bg-gray-700 p-2 rounded-md shadow-md">
          {base36Value.split('').map((digit, index) => (
            <span key={index} className={`transition-colors duration-300 ${steps.length - 1 - index === currentStep ? 'text-blue-500 dark:text-blue-400' : 'dark:text-white'}`}>
              {digit}
            </span>
          ))}
          <sub className="dark:text-white">36</sub>
        </div>
        <div className="flex flex-col items-center space-y-4 mt-16">
          {steps.map((digit, index) => {
            const value = parseInt(digit, 36) * Math.pow(36, index);
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center space-x-4 p-4 rounded-lg shadow-md ${isActive ? 'bg-blue-50 dark:bg-blue-900' :
                    isCompleted ? 'text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'
                  } transition-all duration-300`}
              >
                <div className={`w-12 text-right digit ${isActive ? 'text-blue-500 dark:text-blue-400 font-bold' : 'dark:text-white'} transition-colors duration-300`}>{digit}</div>
                <div className="w-8 text-center dark:text-white">×</div>
                <div className="w-16 text-center group relative dark:text-white">
                  36<sup>{index}</sup>
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-800 text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    36{index > 0 ? <sup>{index}</sup> : ''} = {Math.pow(36, index)}
                  </span>
                </div>
                <div className="w-8 text-center dark:text-white">=</div>
                <div className={`w-20 text-left calculation ${isActive ? 'text-blue-500 dark:text-blue-400 font-bold' : 'dark:text-white'} transition-colors duration-300`}>{value}</div>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: currentStep === steps.length ? 1 : 0, y: currentStep === steps.length ? 0 : 10 }}
            className="font-bold text-xl mt-6 pt-4 border-t border-gray-300 dark:border-gray-600 dark:text-white"
          >
            Sum: {decimalValue} (Decimal)
          </motion.div>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        {showIntermediateConversion && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`mt-6 p-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg shadow-lg relative overflow-hidden`}
            style={{
              background: darkMode
                ? 'linear-gradient(145deg, #2d3748, #1a202c)'
                : 'linear-gradient(145deg, #f7fafc, #edf2f7)'
            }}
          >
            <h4 className="text-2xl font-semibold mb-4 flex items-center">
              <FaCalculator className="mr-2" />
              Base-36 to Decimal Conversion
            </h4>
            <p className="mb-6 leading-relaxed">
              To convert Base-36 to decimal, we multiply each digit by 36 raised to the power of its position (starting from 0) and sum the results.
            </p>

            {renderBase36ToDecimalConversion()}

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                className={`px-4 py-2 flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md transition-all duration-300 transform hover:scale-105`}
                disabled={currentStep === 0}
              >
                <FaChevronLeft className="mr-2" /> Previous
              </button>
              <button
                onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                className={`px-4 py-2 flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md transition-all duration-300 transform hover:scale-105`}
                disabled={currentStep === steps.length}
              >
                Next <FaChevronRight className="ml-2" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }, [showIntermediateConversion, darkMode, currentStep]);

  const getBase36ToDecimalExplanation = useCallback((base36Value, isExample, exampleResult) => {
    const decimalValue = parseInt(base36Value, 36);

    const steps = base36Value.split('').reverse().map((digit, index) => ({
      digit: digit.toUpperCase(),
      power: index,
      value: parseInt(digit, 36) * Math.pow(36, index)
    }));

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Base-36 to Decimal Conversion Works</h3>
        {renderExampleOrConversionText(base36Value, isExample)}
        <p className="mb-4">
          To convert Base-36 to decimal, we multiply each digit by 36 raised to the power of its position (starting from 0) and sum the results.
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Digit</th>
                <th className="px-4 py-2">Position</th>
                <th className="px-4 py-2">Calculation</th>
                <th className="px-4 py-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">{step.digit}</td>
                  <td className="border px-4 py-2">{step.power}</td>
                  <td className="border px-4 py-2">{parseInt(step.digit, 36)} × 36<sup>{step.power}</sup></td>
                  <td className="border px-4 py-2">{step.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4">
          Sum of all values: <span className="font-bold text-purple-500">{decimalValue}</span> (Decimal)
        </p>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{base36Value}</span> (Base-36) = <span className="text-purple-500">{isExample ? exampleResult : decimalValue}</span> (Decimal)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getBase36ToOtherBaseExplanation = useCallback((base36Value, isExample, radix, baseLabel, exampleResult) => {
    const decimalValue = parseInt(base36Value, 36);
    let convertedValue;

    if (baseLabel === 'Base-64') {
      convertedValue = isEncodingMode
        ? specialConversions['Base-64'].encode(decimalValue.toString())
        : specialConversions['Base-64'].fromDecimal(decimalValue);
    } else {
      convertedValue = radix ? decimalValue.toString(radix).toUpperCase() : 'N/A';
    }

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{base36Value}</span><sub className="text-black dark:text-white">36</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{convertedValue}</span>
          {baseLabel !== 'Scientific Notation' && <sub className="text-black dark:text-white">{radix}</sub>}
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Base-36</p>
            <p className="text-lg">{base36Value}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">{baseLabel}</p>
            <p className="text-lg">{convertedValue}</p>
          </div>
        </div>
        <p className="mt-4 text-center text-gray-600 dark:text-gray-300 italic">
          Click "Show Details" for step-by-step explanation
        </p>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Base-36 to {baseLabel} Conversion Works</h3>
        {renderExampleOrConversionText(base36Value, isExample)}
        <p className="mb-4">
          To convert a Base-36 number to {baseLabel}, we first convert the Base-36 number to decimal, and then we convert the decimal to {baseLabel}.
        </p>
        <ConversionFlow
          steps={['Base-36', 'Decimal', baseLabel]}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert Base-36 to decimal: {base36Value}<sub>36</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
            {showIntermediateConversion && (
              <div className="mt-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We convert the Base-36 number to its decimal equivalent.
                </p>
                <p className="font-mono text-center mt-2">
                  <span className="text-blue-500">{base36Value}</span><sub className="text-black">36</sub> = <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub>
                </p>
              </div>
            )}
          </li>
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to {baseLabel}:
            <button
              onClick={toggleDetailedStep2}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetailedStep2 && (
              <ConversionExplanationDecimalToAll
                conversionType="number"
                fromUnit="Decimal"
                toUnit={baseLabel}
                inputValue={decimalValue.toString()}
                result={convertedValue}
                darkMode={darkMode}
                isEncodingMode={isEncodingMode}
                onEncodingModeToggle={onEncodingModeToggle}
              />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <p className="mt-2 text-center">
              <span className="font-bold text-blue-500">{base36Value}<sub className="text-black">36</sub></span> →
              <span className="font-bold text-blue-500">{decimalValue}<sub className="text-black">10</sub></span> →
              <span className="font-bold text-green-500">{convertedValue}<sub className="text-black">{radix}</sub></span>
            </p>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{base36Value}</span> (Base-36) = <span className="text-green-500">{convertedValue}</span> ({baseLabel})
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2, isEncodingMode, onEncodingModeToggle]);

  const getExplanation = useMemo(() => {
    const base36Value = inputValue || '1Z';
    const isExample = !result;
    const exampleResult = isExample ? getExampleResult(base36Value, toUnit) : result;

    if (toUnit === 'Decimal') {
      return getBase36ToDecimalExplanation(base36Value, isExample, exampleResult);
    } else if (toUnit === 'Scientific Notation') {
      return getBase36ToOtherBaseExplanation(base36Value, isExample, null, 'Scientific Notation', exampleResult);
    } else if (toUnit === 'Base-64') {
      // Handle Base-64 separately without using toString(radix)
      return getBase36ToOtherBaseExplanation(
        base36Value,
        isExample,
        null, // Radix not needed
        toUnit,
        exampleResult
      );
    } else {
      return getBase36ToOtherBaseExplanation(
        base36Value,
        isExample,
        toUnit === 'Binary' ? 2 : toUnit === 'Octal' ? 8 : toUnit === 'Hexadecimal' ? 16 : 36, // Use 36 instead of 64
        toUnit,
        exampleResult
      );
    }
  }, [inputValue, toUnit, result, getBase36ToDecimalExplanation, getBase36ToOtherBaseExplanation]);

  useEffect(() => {
    if (showIntermediateConversion) {
      const timer = setTimeout(() => setIsConversionRendered(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsConversionRendered(false);
    }
  }, [showIntermediateConversion]);

  return (
    <div className={`p-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'} font-sans leading-relaxed`}>
      {getExplanation}
      <div className="mt-6">
        <button
          onClick={() => setShowQuiz(!showQuiz)}
          className={`px-4 py-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
        >
          {showQuiz ? 'Hide Quiz' : 'Test Your Knowledge'}
        </button>
      </div>

      {showQuiz && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-4 p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}
        >
          <h4 className="font-semibold">Quick Quiz:</h4>
          <p>Convert the Base-36 number {inputValue || '1Z'} to {toUnit.toLowerCase()}:</p>
          <input
            type="text"
            value={quizAnswer}
            onChange={(e) => setQuizAnswer(e.target.value)}
            className={`mt-2 px-2 py-1 border ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'} rounded-md`}
          />
          <button
            onClick={handleQuizSubmit}
            className={`ml-2 px-4 py-1 ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white rounded-md transition-colors`}
          >
            Submit
          </button>
          {quizResult !== null && (
            <p className={`mt-2 text-lg font-bold ${quizResult ? 'text-green-500' : 'text-red-500'}`}>
              {quizResult ? 'Correct!' : `Try again! The correct answer is ${result}.`}
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default React.memo(ConversionExplanationBase36ToAll);